import { InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Row, Select, theme, Typography } from "antd";

import {
  ContainerComponent,
  InputUnitComponent,
  MarkDownEditorComponent,
  RowComponent
} from "../components";
import { useQuery } from "../hooks/use-query";

import ImageUploader from "../components/image-uploader";
import { useMutation } from "../hooks";
import { SuccessUseCases } from "./success-use-cases.partial";

export const UseCaseFormPartial = ({ method, onSaved, initialValues }) => {
  const [form] = Form.useForm();

  const {
    token: { /* colorInfoDeep, */ colorInfoSeconday },
  } = theme.useToken();

  const { data: categories, loading: loadingCategories } = useQuery({
    url: "/category",
    options: { method: "GET" },
  });

  const { data: typologies, loading: loadingTypologies } = useQuery({
    url: "/typology",
    options: { method: "GET" },
  });

  const { loading: loadingUpload, executeMutation } = useMutation({
    url: "/usecase",
    method: method,
    onSuccess: (result) => {
      onSaved(result);

      form.resetFields();
    },
    onError: (error) => console.log(error),
  });

  const onFinish = async () => {
    const values = await form.validateFields();

    const estimatedDevelopmentTime = values.estimatedDevelopmentTime?.value !== undefined &&
      values.estimatedDevelopmentTime?.unit !== undefined
      ? values.estimatedDevelopmentTime
      : { value: 0, unit: "dias" };

    const currentManualTicketTime = values.currentManualTicketTime?.value !== undefined &&
      values.currentManualTicketTime?.unit !== undefined
      ? values.currentManualTicketTime
      : { value: 0, unit: "horas" };

    const newAgentTicketTime = values.newAgentTicketTime?.value !== undefined &&
      values.newAgentTicketTime?.unit !== undefined
      ? values.newAgentTicketTime
      : { value: 0, unit: "horas" };

    const payload = {
      id: values.id,
      name: values.name,
      idIndustry: values.idIndustry,
      idTypology: values.idTypology,
      description: values.description,
      prompt: values.prompt,
      imageUrl: values.imageUrl,
      diagramFlowUrl: values.diagramFlowUrl,
      estimatedDevelopmentTime: `${estimatedDevelopmentTime.value} ${estimatedDevelopmentTime.unit}`,
      incidentsPerMonth: values.incidentsPerMonth,
      currentManualTicketTime: `${currentManualTicketTime.value} ${currentManualTicketTime.unit}`,
      newAgentTicketTime: `${newAgentTicketTime.value} ${newAgentTicketTime.unit}`,
      steptProblem: values.steptProblem,
      successStories: values.successStories,
    };

    await executeMutation({
      ...payload,
    });
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          estimatedDevelopmentTime: {
            value: initialValues?.estimatedDevelopmentTime.split(" ")[0],
            unit: initialValues?.estimatedDevelopmentTime.split(" ")[1],
          },
          currentManualTicketTime: {
            value: initialValues?.currentManualTicketTime.split(" ")[0],
            unit: initialValues?.currentManualTicketTime.split(" ")[1],
          },
          newAgentTicketTime: {
            value: initialValues?.newAgentTicketTime.split(" ")[0],
            unit: initialValues?.newAgentTicketTime.split(" ")[1],
          },
          successStories: initialValues?.successStories,
        }}
      >
        <ContainerComponent
          style={{
            backgroundColor: "#ffffff",
            padding: 24,
            borderRadius: "0 0 16px 16px",
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.0)",
            marginBottom: 24,
            marginTop: 12
          }}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                name={"id"}
                label="ID"
                style={{ marginBottom: 8, width: "100%", display: "none" }}
              >
                <Input name="id" disabled />
              </Form.Item>
              <Form.Item
                name={"name"}
                label="Nombre del caso de uso"
                style={{ marginBottom: 8, width: "100%" }}
                rules={[
                  {
                    required: true,
                    message: "Requerido",
                  },
                ]}
              >
                <Input name="name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]} justify="start" align="stretch">
            <Col span={8}>
              <Form.Item
                name={"idIndustry"}
                label={"Categoría"}
                style={{ marginBottom: 8, width: "100%" }}
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(
                          "Debe seleccionar al menos una categoría"
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione una categoría"
                  loading={loadingCategories}
                  allowClear
                  filterOption={(input, option) =>
                    option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(value) => form.setFieldsValue({ idIndustry: value })}
                  onDeselect={() => form.setFieldsValue({ idIndustry: null })}
                  options={
                    categories?.map((industry) => ({
                      value: industry.id,
                      label: industry.name,
                    })) || []
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={"idTypology"}
                label={"Tipología"}
                style={{ marginBottom: 8, width: "100%" }}
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(
                          "Debe seleccionar al menos una tipología"
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione una tipología"
                  loading={loadingTypologies}
                  allowClear // Permite deseleccionar
                  filterOption={(input, option) =>
                    option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(value) => form.setFieldsValue({ idTypology: value })}
                  onDeselect={() => form.setFieldsValue({ idTypology: null })}
                  options={
                    typologies?.map((typology) => ({
                      value: typology.id,
                      label: typology.name,
                    })) || []
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={"incidentsPerMonth"}
                label={"Incidencias por mes"}
                type="number"
                rules={[
                  {
                    required: true,
                    message: "Este campo es requerido", // Mensaje de error
                  },
                ]}
              >
                <Input
                  name="incidentsPerMonth"
                  type="number"
                  placeholder="Ej. 10"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]} justify="start" align="stretch">
            <Col span={8}>
              <Form.Item
                name={"currentManualTicketTime"}
                label={"Tiempo de ejecución manual"}
                type="number"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.value || !value.unit) {
                        return Promise.reject("Ambos campos son requeridos");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputUnitComponent
                  name="currentManualTicketTime"
                  type="number"
                  units={["minutos", "horas", "dias", "semanas", "meses"]}
                  placeholder="4"
                  defaultUnit="horas"
                  initialValue={
                    form.getFieldValue("currentManualTicketTime")?.value
                  }
                  initialUnit={
                    form.getFieldValue("currentManualTicketTime")?.unit
                  }
                  onChange={(value) => {
                    form.setFieldsValue({ currentManualTicketTime: value });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={"newAgentTicketTime"}
                label={"Tiempo de ejecución del agente"}
                type="number"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.value || !value.unit) {
                        return Promise.reject("Ambos campos son requeridos");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputUnitComponent
                  name="newAgentTicketTime"
                  type="number"
                  units={["minutos", "horas", "dias", "semanas", "meses"]}
                  placeholder="4"
                  defaultUnit="horas"
                  initialValue={form.getFieldValue("newAgentTicketTime")?.value}
                  initialUnit={form.getFieldValue("newAgentTicketTime")?.unit}
                  onChange={async (value) => {
                    form.setFieldsValue({ newAgentTicketTime: value });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={"estimatedDevelopmentTime"}
                label={"Tiempo de desarrollo"}
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.value || !value.unit) {
                        return Promise.reject("Ambos campos son requeridos");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputUnitComponent
                  name="estimatedDevelopmentTime"
                  type="number"
                  units={["minutos", "horas", "dias", "semanas", "meses"]}
                  placeholder="4"
                  defaultUnit="horas"
                  initialValue={
                    form.getFieldValue("estimatedDevelopmentTime")?.value
                  }
                  initialUnit={
                    form.getFieldValue("estimatedDevelopmentTime")?.unit
                  }
                  onChange={(value) => {
                    form.setFieldsValue({ estimatedDevelopmentTime: value });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                name={"description"}
                label="Descripción del caso de uso"
                required
                style={{ marginBottom: 8, width: "100%" }}
                rules={[{ required: true, message: "Requerido" }]}
              >
                <MarkDownEditorComponent
                  key={"description"}
                  name="description"
                  initialValue={form.getFieldValue("description")}
                  placeHolder="Describa el caso de uso y sus beneficios..."
                  onChange={(value) =>
                    form.setFieldsValue({ description: value.value })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                name={"prompt"} // Corregido de "promt"
                label="Prompt"
                rules={[{ required: true, message: "Requerido" }]}
                style={{ marginBottom: 8, width: "100%" }}
              >
                <MarkDownEditorComponent
                  key={"prompt"}
                  name="prompt"
                  initialValue={form.getFieldValue("prompt")}
                  placeHolder="Describa en detalle el problema que el agente inteligente resolverá..."
                  onChange={(value) =>
                    form.setFieldsValue({ prompt: value.value })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                name={"steptProblem"}
                style={{ width: "100%" }}
                label="Pasos del problema"
              >
                <MarkDownEditorComponent
                  key={"steptProblem"}
                  name="steptProblem"
                  initialValue={form.getFieldValue("steptProblem")}
                  placeHolder="Describa los pasos que son necesarios para resolver el problema"
                  onChange={(value) =>
                    form.setFieldsValue({ steptProblem: value.value })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </ContainerComponent>

        <ContainerComponent
          style={{
            backgroundColor: "#ffffff",
            padding: 24,
            borderRadius: 16,
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
            marginBottom: 24,
          }}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                name="successStories"
                label={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "var(--color-primary)",
                    }}
                  >
                    Caso de éxito
                  </span>
                }
              >
                <SuccessUseCases
                  initialValue={form.getFieldValue("successStories")}
                  onChangeSuccessUseCases={(successUseCases) => {
                    if (successUseCases.length === 0) return;
                    form.setFieldsValue({ successStories: successUseCases });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </ContainerComponent>

        <Row gutter={[16, 16]} justify="start" align="stretch">
          <Col span={12}>
            <ContainerComponent
              style={{
                backgroundColor: "#ffffff",
                padding: 24,
                borderRadius: 16,
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                marginBottom: 24,
                height: "100%",
              }}
            >
              <Row gutter={[16, 16]} justify="start" align="stretch">
                <Col span={12}>
                  <Form.Item name={"imageUrl"} label="Imagen de portada">
                    <ImageUploader
                      initialValue={form.getFieldValue("imageUrl")}
                      onSaved={(imageUrl) =>
                        form.setFieldsValue({ imageUrl: imageUrl })
                      }
                      style={{
                        width: "100%",
                        overflow: "hidden",
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name={"diagramFlowUrl"} label="Imagen del diagrama">
                    <ImageUploader
                      initialValue={form.getFieldValue("diagramFlowUrl")}
                      onSaved={(imageUrl) =>
                        form.setFieldsValue({ diagramFlowUrl: imageUrl })
                      }
                      style={{
                        width: "100%",
                        overflow: "hidden",
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </ContainerComponent>
          </Col>

          <Col span={12}>
            <ContainerComponent
              style={{
                backgroundColor: "var(--color-primary)",
                padding: 24,
                borderRadius: 16,
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                marginBottom: 24,
                textAlign: "start",
                color: "#ffffff",
                height: "100%",
              }}
            >
              <Typography.Text
                style={{
                  fontWeight: "bold",
                  color: "#ffffff" /* colorInfoDeep */,
                }}
              >
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                Recomendaciones:
              </Typography.Text>
              <ul>
                <li>Utilice nombres descriptivos y concisos</li>
                <li>Incluya los beneficios clave en la descripción</li>
                <li>Seleccione imágenes de alta calidad (proporción 1:1)</li>
                <li>Asegúrese de que la categoría sea la más adecuada</li>
                <li>
                  Detalle los pasos del problema de manera clara y secuencial
                </li>
                <li>
                  Incluya métricas reales en los casos de éxito para mayor
                  credibilidad
                </li>
              </ul>
            </ContainerComponent>
          </Col>
        </Row>

        <RowComponent
          style={{
            alignItems: "start",
            width: "100%",
            justifyContent: "space-around",
            marginBottom: 24,
          }}
        ></RowComponent>

        <ContainerComponent
          style={{
            width: "max-content",
            height: "fit-content",
            backgroundColor: colorInfoSeconday,
            borderRadius: 16,
            padding: 16,
            alignItems: "start",
            textAlign: "start",
          }}
        ></ContainerComponent>

        {/* <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <ContainerComponent
              style={{
                backgroundColor: "#ffffff",
                paddingRight: 16,
                borderRadius: 16,
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                marginBottom: 16,
              }}
            >
              <Form.Item style={{ marginTop: 16, textAlign: "end" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loadingUpload}
                  style={{ marginRight: 8, backgroundColor: "var(--color-secondary)" }}
                >
                  Guardar
                </Button>
              </Form.Item>
            </ContainerComponent>
          </Col>
        </Row> */}
      </Form>
      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={loadingUpload}
        style={{
          position: "fixed",
          bottom: 32,
          right: 32,
          zIndex: 2000,
          backgroundColor: "var(--color-secondary)",
          boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
        }}
        onClick={() => form.submit()}
      >
        <SaveOutlined />
        {method === "PUT" ? "Actualizar" : "Guardar"}
      </Button>
    </>
  );
};
